import { useCallback, useMemo } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';
import type { PsfShareCopyBanditType } from '@change-corgi/middle-end/gql';

import { usePsfSharePageData } from 'src/app/pages/psf/share/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useSession } from 'src/app/shared/hooks/session';

import { useReplacements } from './useReplacements';

const DEFAULT_COPY = {
	headingText: translationKey('fe.components.psf.share.initial_headline'),
	subheadingText: translationKey('fe.components.psf.share.initial_subheading'),
	progressBarLabelText: translationKey('fe.components.psf.progress_bar_label'),
	skipLinkText: translationKey('fe.components.psf.share.footer_link_text'),
};

export type TranslationName = keyof typeof DEFAULT_COPY;

const INITIAL_COPY = {
	headingText: translationKey('fe.components.psf.share.signature_count_aa_or_skip_headline'),
	subheadingText: translationKey('fe.components.psf.share.signature_count_subheading'),
} satisfies Partial<Record<TranslationName, TranslationKey>>;
const INITIAL_SHARE_INTENT_COPY = {
	headingText: translationKey('fe.components.psf.share.signature_count_headline'),
} satisfies Partial<Record<TranslationName, TranslationKey>>;

const MESSAGE_COPY = {
	headingText: translationKey('fe.components.psf.share.direct_headline'),
	subheadingText: translationKey('fe.components.psf.share.direct_subheading'),
} satisfies Partial<Record<TranslationName, TranslationKey>>;

function genKeys({
	type,
	shareIntent,
	signatureCount,
}: {
	type: PsfShareCopyBanditType;
	signatureCount: number;
	shareIntent: boolean;
}) {
	switch (type) {
		case 'MESSAGE':
			return { ...DEFAULT_COPY, ...MESSAGE_COPY };
		case 'INITIAL':
			return signatureCount < 5
				? DEFAULT_COPY
				: { ...DEFAULT_COPY, ...INITIAL_COPY, ...(shareIntent ? INITIAL_SHARE_INTENT_COPY : {}) };
		default:
			return DEFAULT_COPY;
	}
}

type Result = ModelHookResult<
	{ keys: Record<TranslationName, TranslationKey> },
	{ translateKey: (name: TranslationName, key: TranslationKey) => string }
>;

export function useCopyI18nKeys(): Result {
	const { translate, translatePlural } = useI18n();
	const {
		data: { petition, type, signaturesFromShareCountEstimate },
	} = usePsfSharePageData();
	const { trackingData } = useSession();
	const {
		data: { replacements },
	} = useReplacements();

	const shareIntent = !!useQueryParamValue('share_intent') && trackingData.current_source !== 'aa_sign_ask';

	return {
		data: {
			keys: useMemo(
				() => genKeys({ type, shareIntent, signatureCount: petition.signatureState.signatureCount.displayed }),
				[type, petition, shareIntent],
			),
		},
		actions: {
			translateKey: useCallback(
				(name: TranslationName, key: TranslationKey): string => {
					// inconsistencies between version of subheadingText, some being pluralized, some not
					if (
						name === 'subheadingText' &&
						(key as unknown as string) === 'fe.components.psf.share.signature_count_subheading'
					) {
						return translatePlural(key, signaturesFromShareCountEstimate, replacements);
					}
					return translate(key, replacements);
				},
				[translate, translatePlural, replacements, signaturesFromShareCountEstimate],
			),
		},
	};
}
